import type { RubricCriterion, RubricScores, BeFeedbackItem, BeSpeechMetrics, KnowledgeManifest } from "@type/training";

export type CallSessionStatus =
    | "pending"
    | "active"
    | "scheduled"
    | "analyzing"
    | "transcribing"
    | "scoring"
    | "results_ready"
    | "failed"
    | "cancelled";

// BE lifecycle: analyzing → transcribing → scoring → results_ready (or → failed)
// Anything that isn't results_ready/failed is still in flight.
export const TERMINAL_CALL_STATUSES: ReadonlyArray<CallSessionStatus> = [
    "results_ready",
    "failed",
    "cancelled",
];

// "Analyzing" tab on the UI lumps together every non-terminal processing state
// because the user doesn't care about the BE's internal pipeline phases.
export const ANALYZING_CALL_STATUSES: ReadonlyArray<CallSessionStatus> = [
    "analyzing",
    "transcribing",
    "scoring",
];

export type OverallGrade = "A" | "B" | "C" | "D" | "F";

export type ActionItemPriority = "high" | "medium" | "low";

export type LatencyPattern = "Stable" | "Degrading" | "Erratic";

export interface TranscriptEntry {
    speaker: "agent" | "user";
    content: string;
    timestamp_secs: number;
    order: number;
    /** Display time label for chat/TXT sessions — the wall-clock HH:MM parsed from
     *  `time_in_chat`. When set, the transcript shows this instead of the elapsed
     *  m:ss derived from `timestamp_secs`. Undefined for audio calls. */
    ts_label?: string;
}

export interface ScoreBreakdown {
    criterion: string;
    score: number;
    max_score: number;
    weight: number;
    notes: string;
}

export interface BotInterruptionDetail {
    timestamp: string;
    overlap_s: number;
    type: "brief_overlap" | "hard_cutoff";
}

export interface TimingMetrics {
    total_duration_s: number;
    total_duration_formatted: string;
    connectivity: {
        pass: boolean;
        bot_audio_detected: boolean;
        candidate_audio_detected: boolean;
        dropout_gaps: string[];
    };
    median_response_time: number;
    avg_response_time: number;
    max_response_time: number;
    max_response_timestamp: string;
    time_to_first_response: number;
    bot_interruptions: {
        count: number;
        brief_overlaps: number;
        hard_cutoffs: number;
        details: BotInterruptionDetail[];
    };
    longest_bot_monologue_s: number;
    longest_bot_monologue_timestamp: string;
    latency_pattern: LatencyPattern;
    latency_std_dev: number;
    latency_p95: number;
    turns_exceeding_3s: number;
    candidate_speaking_s: number;
    bot_speaking_s: number;
    total_response_delays: number;
}

export interface SignalEvidence {
    timestamp: string;
    quote?: string;
    issue?: string;
    transition?: string;
    acknowledged?: boolean;
    ai_words?: string;
    situation?: string;
    ai_response?: string;
    quality?: string;
}

export interface QualitySignal {
    score: number;
    pass: boolean;
    details: string;
    evidence: SignalEvidence[];
    acknowledgement_rate_pct?: number;
}

export interface PromptComplianceScorecard {
    all_required_questions_asked: boolean;
    all_required_questions_asked_details: string;
    interview_structure_followed: boolean;
    interview_structure_followed_details: string;
    persona_maintained: boolean;
    persona_maintained_details: string;
    probing_opportunities: number;
    probing_taken: number;
    probing_details: string;
    within_expected_duration: boolean;
    within_expected_duration_details: string;
}

export interface ActionItem {
    priority: ActionItemPriority;
    description: string;
    category: string;
    technical_suggestion: string;
    location: string;
}

export interface TriageSummary {
    root_cause_hypothesis: string;
    critical_timestamps: string[];
    auto_fixable_issues: number;
    requires_human_review: boolean;
}

export interface LLMResults {
    context_retention: QualitySignal;
    guardrail_compliance: QualitySignal;
    professional_language: QualitySignal;
    candidate_acknowledgement: QualitySignal;
    error_recovery: QualitySignal;
    interview_closure: QualitySignal;
    prompt_compliance: QualitySignal;
    gibberish_tts: QualitySignal;
    conversation_flow_assessment: string;
    action_items: ActionItem[];
    prompt_compliance_scorecard: PromptComplianceScorecard;
    one_line_summary: string;
    triage_summary: TriageSummary;
}

export interface AiqmReport {
    session_id: string;
    overall_grade: OverallGrade;
    overall_score: number;
    one_line_summary: string;
    total_duration_formatted: string;
    median_response_time: number;
    avg_response_time: number;
    max_response_time: number;
    bot_interruptions: number;
    latency_pattern: LatencyPattern;
    candidate_speaking_s: number;
    bot_speaking_s: number;
    action_items_count: number;
    conversation_flow_assessment: string;
    /** Optional — absent for uploaded sessions that have no campaign context. */
    campaign_context?: {
        name: string;
        objective: string;
    };
    timing_metrics: TimingMetrics;
    /** Optional — absent for uploaded sessions with no LLM analysis block. */
    llm_results?: LLMResults;
    // Rubric-based scoring (optional — present only for uploads that
    // configured a rubric at upload time). Snapshotted on the session at
    // upload, never edited after.
    rubric_criteria?: RubricCriterion[];
    rubric_scores?: RubricScores;
    rubric_total_score?: number;
    // Knowledge documents the evaluator was actually given for this result
    // (BE: Trainer KB scoping Phase 0). Absent on legacy results; required
    // non-null on freshly scored uploads once the BE ships the manifest.
    knowledge_manifest?: KnowledgeManifest | null;
    // Coaching feedback items — transcript-linked moments with category and coaching note.
    feedback_items?: BeFeedbackItem[];
    // Speech metrics — computed from agent audio/transcript.
    speech_metrics?: BeSpeechMetrics;
}

export interface CallSession {
    id: string;
    campaign_id: string;
    campaign_name: string;
    contact_id: string | null;
    voice_conversation_id: string | null;
    batch_call_id: string | null;
    contact_name: string | null;
    contact_phone: string | null;
    filename: string | null;
    status: CallSessionStatus;
    duration: number;
    score: number | null;
    sentiment: string | null;
    summary: string | null;
    key_findings: string[];
    risk_signals: string[];
    next_actions: string[];
    category: string | null;
    is_test: boolean;
    audio_url: string | null;
    retry_number: number;
    message_count: number;
    transcript: TranscriptEntry[];
    score_breakdowns: ScoreBreakdown[];
    scheduled_at: string | null;
    started_at: string | null;
    ended_at: string | null;
    created_at: string;
    aiqm_report: AiqmReport | null;
    error_message?: string | null;
}

// The list endpoint returns a slimmer summary per session — flat aiqm_report
// fields, no transcript, no detailed breakdowns. Keep this distinct from
// `CallSession` (used by the single-session endpoint) so the FE can't
// accidentally read a nested field that doesn't exist in the list payload.
export interface CallSessionListItem {
    id: string;
    filename: string | null;
    status: CallSessionStatus;
    duration: number | null;
    overall_grade: OverallGrade | null;
    overall_score: number | null;
    rubric_total_score?: number | null;
    one_line_summary: string | null;
    campaign_id: string | null;
    campaign_name: string | null;
    created_at: string;
    contact_name?: string | null;
    contact_phone?: string | null;
    error_message?: string | null;
    // Free-text tags for grouping/filtering in Insights. May be absent on older
    // BE responses; treat undefined as "no tags".
    tags?: string[] | null;
    // Who uploaded this call. id powers the uploader filter; name is for display.
    // May be absent on older BE responses. See BE_HANDOFF_AIQM_CALL_TAGS.md.
    uploaded_by_id?: string | null;
    uploaded_by_name?: string | null;
    // The trainee who HANDLED (took) this call — independent from uploaded_by
    // (who imported it). `{ id, name }` when assigned, `null`/absent when not.
    // Powers the Handled-by filter + inline assign cell. Source: BE #3193.
    handled_by?: AiqmTrainee | null;
}

// One person who has uploaded AIQM calls — drives the "Uploaded by" filter.
export interface AiqmUploader {
    id: string;
    name: string;
    // Total AI QM sessions uploaded by this user (all pages, no other filters),
    // from GET /training/aiqm/uploaders. Optional for backward-compat with the
    // pre-count response; rendered as `name (count)` when present.
    count?: number;
}

// One trainee who can HANDLE AIQM calls — drives the "Handled by" filter and the
// inline assign menu. Per BE #3193 (decision A), trainees are workspace members,
// so the shape mirrors AiqmUploader. The embedded `handled_by` on a call payload
// only carries `{ id, name }`; `count` is roster metadata for the filter.
export interface AiqmTrainee {
    id: string;
    name: string;
    // Total AI QM calls currently handled by this trainee (roster-wide, no other
    // filters), from the trainees list endpoint. Optional for backward-compat;
    // rendered as `name (count)` in the filter when present.
    count?: number;
}

export const SIGNAL_LABELS: Record<string, string> = {
    context_retention: "Context Retention",
    guardrail_compliance: "Guardrail Compliance",
    professional_language: "Professional Language",
    candidate_acknowledgement: "Acknowledgement",
    error_recovery: "Error Recovery",
    interview_closure: "Interview Closure",
    prompt_compliance: "Prompt Compliance",
    gibberish_tts: "Speech Quality",
};

export const GRADE_CONFIG: Record<OverallGrade, { label: string; color: string; bg: string }> = {
    A: { label: "Excellent", color: "text-emerald-700", bg: "bg-emerald-50" },
    B: { label: "Good", color: "text-blue-700", bg: "bg-blue-50" },
    C: { label: "Acceptable", color: "text-amber-700", bg: "bg-amber-50" },
    D: { label: "Needs Improvement", color: "text-orange-700", bg: "bg-orange-50" },
    F: { label: "Poor", color: "text-rose-700", bg: "bg-rose-50" },
};

export function scoreColor(score: number): string {
    if (score >= 80) return "text-emerald-600";
    if (score >= 60) return "text-amber-600";
    return "text-rose-600";
}

export function scoreBg(score: number): string {
    if (score >= 80) return "bg-emerald-50";
    if (score >= 60) return "bg-amber-50";
    return "bg-rose-50";
}

export function scoreBarColor(score: number): string {
    if (score >= 80) return "bg-emerald-500";
    if (score >= 60) return "bg-amber-500";
    return "bg-rose-500";
}

// Returns a color scheme for an action-item priority. Defensive against
// unknown values (BE may evolve the priority enum or return null) — falls
// back to the "medium" treatment rather than crashing the render.
export function priorityColor(priority: ActionItemPriority | string | null | undefined): { text: string; bg: string } {
    switch (priority) {
        case "high":
            return { text: "text-rose-700", bg: "bg-rose-50" };
        case "low":
            return { text: "text-emerald-700", bg: "bg-emerald-50" };
        case "medium":
        default:
            return { text: "text-amber-700", bg: "bg-amber-50" };
    }
}

// ───────────────────────────────────────────────────────────────────────────
// Aggregated Sentiment Analysis (AIQM "Insights" tab)
//
// Cross-call aggregate that lets a client's Ops/CX manager see what hundreds
// to thousands of calls are collectively saying — a sentiment trend over time
// plus the top recurring (free-form, LLM-extracted) themes.
//
// IMPORTANT: these aggregate shapes are DISTINCT from the per-call `sentiment`
// field on CallSession. A single call has a categorical sentiment; an aggregate
// spans many calls, so it carries numeric scores + percentage breakdowns. The
// categorical `SentimentLevel` (from @type/agent) is still reused, but only to
// render a chip color by bucketing a numeric avg_sentiment — never to type the
// aggregate numbers themselves.
//
// BACKEND STATUS: nothing exists yet. The frontend builds against this contract
// with a mock implementation (see services/aiqm/sentiment) and swaps to the real
// endpoint when it lands. The response is always PRE-AGGREGATED by the backend —
// the frontend never fetches per-call data to compute these.
// ───────────────────────────────────────────────────────────────────────────

export type SentimentGranularity = "daily" | "weekly" | "monthly";

export const SENTIMENT_GRANULARITIES: ReadonlyArray<SentimentGranularity> = [
    "daily",
    "weekly",
    "monthly",
];

// Caps baked into the contract so the payload stays small and fixed regardless
// of underlying call volume. The frontend trusts the backend to honor these but
// also defends against larger arrays (slices) so a misbehaving BE can't hang the
// render.
export const MAX_SENTIMENT_THEMES = 20;
export const MAX_SENTIMENT_TREND_BUCKETS = 365;
// Representative drill-down calls shown per theme. Capped so an early/misbehaving
// backend returning hundreds of ids can't render hundreds of buttons in one
// expanded theme row and bog down the Insights tab.
export const MAX_THEME_EXAMPLE_CALLS = 10;

// Below this many calls in the selected range, a trend line / theme ranking is
// statistically meaningless and would mislead. The UI shows a "not enough data"
// state instead. (A single trend point is also treated as sparse.)
export const MIN_CALLS_FOR_INSIGHTS = 10;

// One point on the sentiment-over-time chart. `net_sentiment` is the headline
// metric an Ops/CX manager skims: (% positive − % negative), range -100..100.
export interface SentimentTrendBucket {
    // ISO date marking the START of the bucket (day/week/month per granularity).
    bucket_start: string;
    net_sentiment: number; // -100..100
    call_count: number;
}

export interface SentimentBreakdown {
    positive: number; // percentages, 0..100, sum ~= 100
    neutral: number;
    negative: number;
}

// One recurring theme across the call set. `label` is a free-form, LLM-extracted
// string (NOT from a fixed taxonomy) — render defensively: it may be long, may
// rename/split between periods, and must never be used as a stable key for
// cross-period comparison. Use `id` for React keys.
export interface SentimentTheme {
    id: string;
    label: string;
    call_count: number;
    avg_sentiment: number; // -100..100
    breakdown: SentimentBreakdown;
    // Up to a handful of representative call session ids for drill-down into the
    // existing AIQM detail page. May be empty.
    example_call_ids: string[];
    // Pre-computed by the BE — the FE renders these directly, no recomputing.
    // See AIQM_INSIGHTS_PRECOMPUTED_FIELDS_BE_HANDOFF.
    call_pct: number; // round(call_count / total_calls * 100)
    sentiment_level: "positive" | "neutral" | "negative";
}

export interface AiqmSentimentAggregate {
    granularity: SentimentGranularity;
    // Echoed-back range the aggregate covers (ISO dates).
    start_date: string;
    end_date: string;
    // Total calls that fed this aggregate across the whole range. Drives the
    // sparse/empty gating via MIN_CALLS_FOR_INSIGHTS.
    total_calls: number;
    // Single headline net sentiment across the whole range (-100..100).
    overall_net_sentiment: number;
    overall_breakdown: SentimentBreakdown;
    trend: SentimentTrendBucket[];
    themes: SentimentTheme[];
    // Pre-computed by the BE — the FE renders these directly, no recomputing.
    // See AIQM_INSIGHTS_PRECOMPUTED_FIELDS_BE_HANDOFF.
    net_sentiment_level: "positive" | "neutral" | "negative";
    momentum: number; // latest trend bucket net − previous bucket net
    theme_split: { positive: number; negative: number };
}

// One theme whose sentiment moved most vs the previous period (pre-computed by BE).
export interface SentimentMover {
    id: string;
    label: string;
    avg_sentiment: number; // current period
    delta: number; // current − previous avg_sentiment
}

// Period-over-period figures the BE computes when compare=true.
export interface SentimentComparison {
    net_sentiment_delta: number; // current − previous overall_net_sentiment
    total_calls_delta: number; // current − previous total_calls
    movers: SentimentMover[]; // top movers, pre-sorted by |delta| desc
}

// Maps a numeric aggregate sentiment score (-100..100) onto the categorical
// SentimentLevel used by the shared sentiment chip helpers. Mixed isn't
// inferable from a single net number, so we never return it here.
export function netSentimentToLevel(
    score: number
): "positive" | "neutral" | "negative" {
    if (score >= 20) return "positive";
    if (score <= -20) return "negative";
    return "neutral";
}

// Human-readable label for a granularity (for selectors / headings).
export const GRANULARITY_LABELS: Record<SentimentGranularity, string> = {
    daily: "Daily",
    weekly: "Weekly",
    monthly: "Monthly",
};

// --- Bulk audio upload progress WebSocket (#2517 / BE #3090) ---------------
// Mirrors the bulk-CV upload WS. The BE streams one `progress` event per file
// as it resolves; the FE matches `batch_id` and updates that file's row + the
// `current`/`total` indicator.
export interface AiqmUploadWSConnectedMessage {
    event: "connected";
    message?: string;
}

export interface AiqmUploadWSProgressMessage {
    // `event` is the BATCH signal; `status` is the PER-FILE result — they are
    // ORTHOGONAL (updated BE contract, ourteam-backend #3575/#3576/#3579):
    //   progress   → a file SUCCEEDED, batch not done
    //   error      → a file FAILED, batch not done
    //   processing → a live per-file sub-status update (no counter change)
    //   completed  → EVERY file handled → show the completion banner (fires even
    //                when the last file failed)
    // "cancelled" kept for backward-compat with the old vocabulary.
    event: "processing" | "progress" | "error" | "completed" | "cancelled";
    batch_id: string;
    // AIQM correlates by the call session id (NOT the bulk-CV `temp_cv_id`).
    session_id: string;
    // PER-FILE status — render the file's tick from THIS, never from `event`:
    //   completed / success        → done (green tick)
    //   failed / cancelled / duplicate → terminal failure (errorType distinguishes)
    //   processing / transcribing / scoring → still in flight (spinner + sub-status)
    // Old values (success/cancelled/duplicate) retained so the FE tolerates both
    // the old and new BE while the contract rolls out.
    status:
        | "processing"
        | "transcribing"
        | "scoring"
        | "success"
        | "completed"
        | "failed"
        | "cancelled"
        | "duplicate";
    // `current` = HANDLED count (every file that reached a terminal state,
    // success OR failure) — drives the progress bar / completion.
    current: number;
    // `processed` = SUCCEEDED count (subset of `current`) — the "X succeeded of
    // Y" summary. Optional so the FE tolerates old frames that omit it.
    processed?: number;
    total: number;
    // Human-readable reason streamed by the BE (mirrors the bulk-CV `message`).
    // On a failure it carries the reason the FE classifies on (e.g. "not a CS
    // call" → not_cs_call). Optional so the FE tolerates events that omit it.
    message?: string;
    filename?: string;
}

export type AiqmUploadWSMessage =
    | AiqmUploadWSConnectedMessage
    | AiqmUploadWSProgressMessage;
